/**
 * Strategic-impact templates per criterion — the CEO-facing translation of a
 * score. Each line is a { headline, soWhat } pair: an impact-first headline and
 * one consequence sentence. The mechanical evidence (score, weight, slots) is
 * carried separately on the derived bullet and shown as drill-down.
 *
 * Keyed by criterion slug + sign. A missing key → null (graceful degradation).
 * Content reviewed 2026-06 against the LIVE rubric (single-operator framing:
 * Yango is the sole operator; B2B customers consume Yango Deli's services and
 * do not run their own tenant). See [[project-assessment-rubric-live-source]].
 */

export type StrategicSign = "pro" | "con";

export interface StrategicLine {
  headline: string;
  soWhat: string;
}

interface StrategicTemplate {
  pro?: StrategicLine;
  con?: StrategicLine;
}

const PER_CRITERION: Record<string, StrategicTemplate> = {
  "time-to-mvp": {
    pro: {
      headline: "Live within a quarter.",
      soWhat:
        "A working B2B pilot ships in ~13 weeks — the funding window is respected and you get market signal before burning runway.",
    },
    con: {
      headline: "Pre-revenue period stretches past a quarter.",
      soWhat:
        "Months of integration before the first customer goes live — you staff and spend ahead of any market signal.",
    },
  },
  "tco-at-scale": {
    pro: {
      headline: "Unit economics hold at scale.",
      soWhat:
        "Cost per user / transaction stays flat as you grow — scaling the business doesn't disproportionately scale the platform bill.",
    },
    con: {
      headline: "Cheap now, expensive at scale.",
      soWhat:
        "Per-MAU and per-transaction costs compress margin exactly when you're growing — the 10k-user price hides the 1M-user bill.",
    },
  },
  "vendor-financial-health": {
    pro: {
      headline: "The platform will still be here in five years.",
      soWhat:
        "A durable vendor — or an open-source core you can self-host — lets you commit on a multi-year horizon instead of hedging against a mid-deployment pivot.",
    },
    con: {
      headline: "Vendor durability is its own risk.",
      soWhat:
        "A failure or strategic pivot mid-deployment leaves Yango holding the integration — mitigate with source-availability or escrow language at signature.",
    },
  },
  "extensibility-headroom": {
    pro: {
      headline: "Build in parallel, not in a vendor's queue.",
      soWhat:
        "Deep extensibility lets Moveo ship integration and product work simultaneously — no professional-services ticket on the critical path.",
    },
    con: {
      headline: "Roadmap capped by the vendor's PS queue.",
      soWhat:
        "Anything not configurable in-product becomes a vendor ticket Moveo can't parallelise — velocity is gated externally.",
    },
  },
  "market-credibility-maturity": {
    pro: {
      headline: "A playbook already runs at scale.",
      soWhat:
        "Tier-1 references mean Yango isn't the test case — large rollouts have a proven path.",
    },
    con: {
      headline: "Yango becomes the test case.",
      soWhat:
        "First-of-kind deployment risk — you'll drive product fixes that should already exist and be the vendor's largest customer.",
    },
  },
  "multi-tenant-isolation": {
    pro: {
      headline: "Per-tenant isolation, if you ever need it.",
      soWhat:
        "Optionality for a future white-label model. Not a buying criterion today — Yango is the sole operator.",
    },
    con: {
      headline: "Single-tenant only — acceptable for now.",
      soWhat:
        "Fine while Yango is the sole operator; kept in the rubric at weight 0 as an honest data point.",
    },
  },
  "data-ownership-exit-cost": {
    pro: {
      headline: "Your consumer data stays portable.",
      soWhat:
        "Documented export keeps exit a real option — vendor leverage stays in check over the multi-year horizon.",
    },
    con: {
      headline: "Lock-in on your most valuable asset.",
      soWhat:
        "No export path means a long deployment accumulates lock-in on Israeli consumer data — mitigation must be a contract clause at signature, not a retrofit.",
    },
  },
  "identity-federation": {
    pro: {
      headline: "One Yango identity, end to end.",
      soWhat:
        "Federates with Yango auth (OAuth/OIDC/SAML) — users sign in once; support and analytics see a single identity.",
    },
    con: {
      headline: "Two account systems — the super-app thesis breaks.",
      soWhat:
        "Can't federate with Yango Passport, so every consumer gets a separate account and the loyalty backbone fragments.",
    },
  },
  "israeli-market-fit": {
    pro: {
      headline: "IL-native from day one.",
      soWhat:
        "Hebrew RTL, ILS settlement and IL processors (Tranzila, Pelecard, CardCom) are baked in — no bolt-on work just to be Israel-ready.",
    },
    con: {
      headline: "Not built for Israeli retail.",
      soWhat:
        "Every IL rail is a bolt-on or custom build before launch — and a dealbreaker if RTL / ILS simply aren't possible.",
    },
  },
  "israeli-compliance-posture": {
    pro: {
      headline: "Audit-ready for chain customers.",
      soWhat:
        "Privacy Law, Accessibility 5568, PCI scope and tax-receipt obligations are met — deals close without a compliance bottleneck.",
    },
    con: {
      headline: "Compliance gaps block the revenue model.",
      soWhat:
        "PPL / 5568 / PCI / חשבונית מס gaps must close before a chain can sign — and chains are the revenue.",
    },
  },
  "admin-ux-merchants": {
    pro: {
      headline: "Non-technical staff run it themselves.",
      soWhat:
        "The people running day-to-day campaigns configure them without engineers — training is a one-time cost, not a recurring drag.",
    },
    con: {
      headline: "A hostile admin UI fails in production.",
      soWhat:
        "Strong dev story, but operators can't self-serve — every new B2B customer and staff change becomes a ramp-up cost.",
    },
  },
  "roadmap-executability": {
    pro: {
      headline: "The team can deliver this as-is.",
      soWhat:
        "Hireable IL talent and SI/partner presence mean no major hiring on the critical path.",
    },
    con: {
      headline: "The plan outruns the team.",
      soWhat:
        "Delivering this stack at the planned pace needs talent that's hard to staff in IL — either the plan slips or the team must grow.",
    },
  },
  "il-ecosystem-marketplace": {
    pro: {
      headline: "Onboarding a new B2B customer is a config step.",
      soWhat:
        "A mature IL connector ecosystem (Fourth, Caspit, Priority, Comax) makes wiring a hotel's or restaurant's POS / accounting plug-in, not project.",
    },
    con: {
      headline: "Every B2B-customer integration is a custom build.",
      soWhat:
        "No marketplace, no shared playbook — each new hotel / restaurant / mall onboarding pays full integration cost, so it never amortises.",
    },
  },
};

const COVERAGE_TEMPLATES: Record<string, StrategicTemplate> = {
  "b2c-feature-coverage": {
    pro: {
      headline: "B2C is solved out of the box.",
      soWhat:
        "Most of the consumer set (promotions, loyalty, profiles) ships native — engineering spends on differentiation, not parity.",
    },
    con: {
      headline: "B2C parity is build-it-yourself.",
      soWhat:
        "Most consumer features need custom work before launch — the pipeline is catch-up before any differentiation.",
    },
  },
  "non-mvp-b2b-feature-coverage": {
    pro: {
      headline: "Headroom for the months 9–18 roadmap.",
      soWhat:
        "The long-tail B2B features are already covered — you won't hit a wall right after the MVP ships.",
    },
    con: {
      headline: "Runs out of headroom after MVP.",
      soWhat:
        "The non-MVP B2B long tail isn't covered — you'll be building (or switching vendors) right when customers expect depth.",
    },
  },
  "all-in-one-coverage": {
    pro: {
      headline: "One platform, fewer vendors to wire.",
      soWhat:
        "Breadth across CRM + loyalty + comms + analytics cuts vendor count and integration labour.",
    },
    con: {
      headline: "A point solution, not a suite.",
      soWhat: "Narrow breadth means more vendors and more integration glue to own.",
    },
  },
};

export function strategicForCriterion(slug: string, sign: StrategicSign): StrategicLine | null {
  return PER_CRITERION[slug]?.[sign] ?? null;
}

export function strategicForCoverage(slug: string, sign: StrategicSign): StrategicLine | null {
  return COVERAGE_TEMPLATES[slug]?.[sign] ?? null;
}
